'use client';

import { createContext, useCallback, useContext, useState, type ReactNode } from 'react';
import { createClient } from '@/lib/supabase/client';
import { safeReturnTo } from '@/lib/safe-return-to';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { Cake, Loader2 } from 'lucide-react';

/* ------------------------------------------------------------------ */
/*  Context                                                            */
/* ------------------------------------------------------------------ */

interface AuthModalContextValue {
  openAuthModal: (mode?: 'login' | 'signup', returnTo?: string) => void;
  closeAuthModal: () => void;
}

const AuthModalContext = createContext<AuthModalContextValue>({
  openAuthModal: () => {},
  closeAuthModal: () => {},
});

export const useAuthModal = () => useContext(AuthModalContext);

/* ------------------------------------------------------------------ */
/*  Provider                                                           */
/* ------------------------------------------------------------------ */

export function AuthModalProvider({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState<'login' | 'signup'>('signup');
  const [returnTo, setReturnTo] = useState<string | undefined>();

  const openAuthModal = useCallback((m: 'login' | 'signup' = 'signup', rt?: string) => {
    setMode(m);
    setReturnTo(rt);
    setOpen(true);
  }, []);

  const closeAuthModal = useCallback(() => setOpen(false), []);

  return (
    <AuthModalContext.Provider value={{ openAuthModal, closeAuthModal }}>
      {children}
      <AuthModal open={open} onOpenChange={setOpen} mode={mode} setMode={setMode} returnTo={returnTo} />
    </AuthModalContext.Provider>
  );
}

/* ------------------------------------------------------------------ */
/*  Modal                                                              */
/* ------------------------------------------------------------------ */

function AuthModal({
  open,
  onOpenChange,
  mode,
  setMode,
  returnTo,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  mode: 'login' | 'signup';
  setMode: (m: 'login' | 'signup') => void;
  returnTo?: string;
}) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [info, setInfo] = useState('');
  const canUseDevAuth = process.env.NODE_ENV === 'development';

  const reset = () => {
    setName('');
    setEmail('');
    setPassword('');
    setError('');
    setInfo('');
    setLoading(false);
  };

  const handleOpenChange = (v: boolean) => {
    if (!v) reset();
    onOpenChange(v);
  };

  const safeTarget = safeReturnTo(returnTo);
  const getRedirectUrl = () => safeTarget ?? '/marketplace';

  const createDevSession = async (): Promise<boolean> => {
    if (!canUseDevAuth) return false;
    const normalizedEmail = email.trim().toLowerCase();

    const res = await fetch('/api/proxy/auth/dev-login', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email: normalizedEmail, password }),
    });

    if (!res.ok) return false;

    const data = await res.json();
    document.cookie = `sweetspot_dev_auth=${encodeURIComponent(JSON.stringify({ userId: data.userId, email: data.email }))};path=/;max-age=${60 * 60 * 24 * 7}`;
    return true;
  };

  /* ── Login ── */
  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    const normalizedEmail = email.trim().toLowerCase();

    const supabase = createClient();
    const { error: supabaseError } = await supabase.auth.signInWithPassword({ email: normalizedEmail, password });

    if (!supabaseError) {
      window.location.href = getRedirectUrl();
      return;
    }

    if (!canUseDevAuth) {
      setError(supabaseError.message);
      setLoading(false);
      return;
    }

    try {
      const sessionCreated = await createDevSession();
      if (!sessionCreated) {
        setError(supabaseError.message);
        setLoading(false);
        return;
      }
      window.location.href = getRedirectUrl();
    } catch {
      setError('Unable to reach local auth server. Please ensure API is running on http://localhost:4000.');
      setLoading(false);
    }
  };

  /* ── Signup ── */
  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    const normalizedEmail = email.trim().toLowerCase();

    try {
      const res = await fetch('/api/proxy/auth/signup', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, email: normalizedEmail, password }),
      });

      if (!res.ok) {
        const data = await res.json();
        const message =
          typeof data?.message === 'string' ? data.message : 'Failed to create account';
        const isAlreadyRegistered = message
          .toLowerCase()
          .includes('user already registered');
        if (isAlreadyRegistered) {
          const supabase = createClient();
          const { error: signInError } = await supabase.auth.signInWithPassword({
            email: normalizedEmail,
            password,
          });
          if (!signInError) {
            window.location.href = getRedirectUrl();
            return;
          }
          setMode('login');
          setError(
            'This email is already registered. Please sign in or reset your password.',
          );
          setLoading(false);
          return;
        }
        setError(message);
        setLoading(false);
        return;
      }

      const result = await res.json();

      if (safeTarget) sessionStorage.setItem('sweetspot_return_to', safeTarget);

      if (result.mode === 'admin') {
        const supabase = createClient();
        const { error: signInError } = await supabase.auth.signInWithPassword({ email: normalizedEmail, password });
        if (signInError) { setError(signInError.message); setLoading(false); return; }
        window.location.href = '/onboarding';
        return;
      }

      if (result.mode === 'anon') {
        if (result.emailConfirmRequired) {
          setInfo('Account created! Please check your email to confirm, then sign in.');
          setLoading(false);
          return;
        }
        const supabase = createClient();
        const { error: signInError } = await supabase.auth.signInWithPassword({ email: normalizedEmail, password });
        if (signInError) { setError(signInError.message); setLoading(false); return; }
        window.location.href = '/onboarding';
        return;
      }

      if (result.mode === 'dev') {
        const sessionCreated = await createDevSession();
        if (!sessionCreated) {
          setError('Dev auth is disabled. Enable AUTH_DEV_BYPASS_ENABLED=true in development to use local auth.');
          setLoading(false);
          return;
        }
        window.location.href = '/onboarding';
        return;
      }

      window.location.href = '/onboarding';
    } catch {
      setError('Something went wrong. Please try again.');
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-sm p-0 gap-0 overflow-hidden">
        {/* Header */}
        <div className="flex flex-col items-center pt-8 pb-4 px-6">
          <div className="mb-3 flex h-11 w-11 items-center justify-center rounded-full bg-primary/10">
            <Cake className="h-5 w-5 text-primary" />
          </div>
          <h2 className="text-lg font-semibold">
            {mode === 'login' ? 'Welcome back' : 'Create your account'}
          </h2>
          <p className="text-sm text-muted-foreground mt-0.5">
            {mode === 'login' ? 'Sign in to continue' : 'Get started for free'}
          </p>
        </div>

        {/* Form */}
        <form onSubmit={mode === 'login' ? handleLogin : handleSignup} className="px-6 pb-6 space-y-4">
          {error && (
            <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">{error}</div>
          )}
          {info && (
            <div className="rounded-md bg-primary/10 p-3 text-sm text-primary">{info}</div>
          )}

          {mode === 'signup' && (
            <div className="space-y-1.5">
              <Label htmlFor="auth-name" className="text-xs">Name</Label>
              <Input
                id="auth-name"
                placeholder="Your name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                className="h-9"
              />
            </div>
          )}

          <div className="space-y-1.5">
            <Label htmlFor="auth-email" className="text-xs">Email</Label>
            <Input
              id="auth-email"
              type="email"
              placeholder="you@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="h-9"
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="auth-password" className="text-xs">Password</Label>
            <Input
              id="auth-password"
              type="password"
              placeholder={mode === 'signup' ? 'At least 8 characters' : ''}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              minLength={mode === 'signup' ? 8 : undefined}
              className="h-9"
            />
          </div>

          <Button type="submit" className="w-full" disabled={loading}>
            {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
            {mode === 'login' ? 'Sign In' : 'Sign Up'}
          </Button>

          <p className="text-center text-xs text-muted-foreground">
            {mode === 'login' ? "Don't have an account? " : 'Already have an account? '}
            <button
              type="button"
              className="text-primary hover:underline font-medium"
              onClick={() => { reset(); setMode(mode === 'login' ? 'signup' : 'login'); }}
            >
              {mode === 'login' ? 'Sign up' : 'Sign in'}
            </button>
          </p>
        </form>
      </DialogContent>
    </Dialog>
  );
}
